/**
 * Scheduler service — booking links, availability rules, bookings.
 *
 * Slot computation is client-side: given a booking link + a date,
 * we pull the studio's availability rules + existing confirmed
 * bookings for that day, then yield the open slots.
 *
 * Timezone handling for v1 is local-time of the studio. v2 will
 * read a tz column from organizations and convert correctly across
 * the public-page viewer / studio-app boundaries.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { demoBookingLinks } from '@/data/demoOverlay';

// ─── Types ───────────────────────────────────────────────────────

export type LocationType = 'video' | 'phone' | 'in_person' | 'custom';

export interface BookingLink {
  id: string;
  org_id: string;
  slug: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  location_type: LocationType;
  location_details: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AvailabilityRule {
  id: string;
  org_id: string;
  day_of_week: number; // 0=Sun, 6=Sat
  start_time: string;  // "09:00"
  end_time: string;    // "17:00"
  created_at: string;
}

export type BookingStatus = 'confirmed' | 'cancelled' | 'completed' | 'no_show';

export interface Booking {
  id: string;
  org_id: string;
  booking_link_id: string | null;
  start_at: string;
  end_at: string;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  notes: string | null;
  status: BookingStatus;
  inquiry_id: string | null;
  created_at: string;
}

// ─── Booking links: CRUD ─────────────────────────────────────────

export const useBookingLinks = () => {
  const { data: orgId } = useOrgId();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  return useQuery({
    queryKey: ['booking-links', orgId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (isDemo) {
        const rows = demoStore.bookingLinks.list();
        return (rows.length > 0 ? rows : demoBookingLinks) as unknown as BookingLink[];
      }
      if (!orgId) return [] as BookingLink[];
      const { data, error } = await supabase
        .from('booking_links')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as BookingLink[];
    },
    enabled: isDemo || !!orgId,
  });
};

/**
 * Public booking page: resolve a link by slug.
 *
 * Tenant-isolation fix — the old broad `is_active = true` SELECT policy
 * on booking_links let any signed-in account read every studio's links.
 * The table is now org-scoped; public visitors go through the
 * get_public_booking_link_by_slug SECURITY DEFINER RPC, which returns
 * only the allowlisted public fields of a single active link.
 */
export const useBookingLinkBySlug = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['booking-link-by-slug', slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await (supabase as any).rpc('get_public_booking_link_by_slug', {
        p_slug: slug,
      });
      if (error) throw error;
      if (!data) throw new Error('Booking link not found');
      return data as BookingLink;
    },
    enabled: !!slug,
  });
};

/**
 * Public booking page: opening hours for the link's studio, read via
 * the public_booking_availability definer RPC (availability_rules is
 * org-scoped and no longer readable cross-tenant).
 */
export const usePublicAvailabilityRules = (linkId: string | undefined) => {
  return useQuery({
    queryKey: ['public-availability-rules', linkId],
    queryFn: async () => {
      if (!linkId) return [] as AvailabilityRule[];
      const { data, error } = await (supabase as any).rpc('public_booking_availability', {
        p_link_id: linkId,
      });
      if (error) throw error;
      return (data ?? []) as AvailabilityRule[];
    },
    enabled: !!linkId,
  });
};


/**
 * Fetch a studio's IANA timezone for the public booking page.
 * Defaults to "UTC" if the org hasn't set one yet — matches the
 * column default in the 20260525_scheduler_timezone.sql migration.
 *
 * SEC-1 lockdown: the anon SELECT on `organizations` is gone (it
 * exposed Stripe/subscription columns). We read through the
 * get_public_org_by_id RPC instead — hand-built public allowlist,
 * works for anon AND authenticated callers.
 */
export const useStudioTimezone = (orgId: string | undefined) => {
  return useQuery({
    queryKey: ['studio-timezone', orgId],
    queryFn: async (): Promise<string> => {
      if (!orgId) return 'UTC';
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { data } = await (supabase as any).rpc('get_public_org_by_id', {
        _org_id: orgId,
      });
      return (data?.timezone as string | undefined) ?? 'UTC';
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
};

/**
 * Fetch the studio's display branding (label + name + logo + brand color)
 * for public surfaces.
 *
 * SEC-1 lockdown: was a `select('*')` on organizations, which anon can
 * no longer do. get_public_org_by_id returns the public allowlist
 * (name, logos, brand_color, studio_label, …) so the fallback chain
 * below keeps working unchanged.
 */
export const useStudioBranding = (orgId: string | undefined) => {
  return useQuery({
    queryKey: ['studio-branding', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { data } = await (supabase as any).rpc('get_public_org_by_id', {
        _org_id: orgId,
      });
      if (!data) return null;
      const row = data as Record<string, unknown>;
      const label =
        (typeof row.studio_label === 'string' && row.studio_label.trim()) ||
        (typeof row.name === 'string' && row.name) ||
        'Business';
      return {
        label: label as string,
        name: (row.name as string | null) ?? null,
        // GALLERY-BAR — `brand_logo_url` first.
        //
        // This read `logo_url` alone, while the gallery EDITOR's live
        // preview has always resolved `brand_logo_url ?? logo_url`.
        // So a studio who set a logo in Brand saw it on the preview
        // and their client did not see it on the gallery — which is
        // the likeliest reading of Rennan asking to "add in your logo
        // from your brand" for a bar that already drew a logo.
        //
        // `brand_logo_dark_url` and `brand_logo_light_url` exist too
        // and are NOT used here: whether "light" names the logo's own
        // colour or the ground it is meant for decides which one goes
        // on a dark gallery, and guessing wrong puts a black mark on a
        // black page. Left for someone who knows which it is.
        logoUrl:
          (row.brand_logo_url as string | null) ?? (row.logo_url as string | null) ?? null,
        brandColor: (row.brand_color as string | null) ?? null,
      };
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });
};

export const useCreateBookingLink = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  return useMutation({
    mutationFn: async (input: Partial<BookingLink> & { name: string; slug: string }) => {
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      const { data, error } = await supabase
        .from('booking_links')
        .insert({ ...input, org_id: orgId })
        .select()
        .single();
      if (error) throw error;
      return data as BookingLink;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-links'] });
    },
  });
};

export const useUpdateBookingLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<BookingLink> & { id: string }) => {
      const { error } = await supabase
        .from('booking_links')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-links'] });
    },
  });
};

export const useDeleteBookingLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('booking_links').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-links'] });
    },
  });
};

// ─── Availability rules: CRUD ────────────────────────────────────

export const useAvailabilityRules = (orgIdOverride?: string) => {
  const { data: defaultOrgId } = useOrgId();
  const orgId = orgIdOverride ?? defaultOrgId;
  return useQuery({
    queryKey: ['availability-rules', orgId],
    queryFn: async () => {
      if (!orgId) return [] as AvailabilityRule[];
      const { data, error } = await supabase
        .from('availability_rules')
        .select('*')
        .eq('org_id', orgId)
        .order('day_of_week', { ascending: true });
      if (error) throw error;
      return (data ?? []) as AvailabilityRule[];
    },
    enabled: !!orgId,
  });
};

export const useUpsertAvailabilityRule = () => {
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  return useMutation({
    mutationFn: async (input: { id?: string; day_of_week: number; start_time: string; end_time: string }) => {
      if (!orgId) throw new Error('Your studio isn’t set up yet');
      if (input.id) {
        const { error } = await supabase
          .from('availability_rules')
          .update({
            day_of_week: input.day_of_week,
            start_time: input.start_time,
            end_time: input.end_time,
          })
          .eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('availability_rules')
          .insert({ ...input, org_id: orgId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rules'] });
    },
  });
};

export const useDeleteAvailabilityRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('availability_rules').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rules'] });
    },
  });
};

// ─── Bookings: read studio side + create public side ─────────────

export const useBookings = () => {
  const { data: orgId } = useOrgId();
  return useQuery({
    queryKey: ['bookings', orgId],
    queryFn: async () => {
      if (!orgId) return [] as Booking[];
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('org_id', orgId)
        .order('start_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Booking[];
    },
    enabled: !!orgId,
  });
};

/**
 * Fetch existing bookings for a specific link in a date range —
 * used by the public booking page to know which slots are taken.
 *
 * SEC-1 lockdown: the anon SELECT on `bookings` is gone (it exposed
 * client_email / client_phone / notes). public_booking_slots returns
 * only { id, start_at, end_at } for confirmed bookings on active
 * links — exactly what computeAvailableSlots needs. We synthesize
 * status: 'confirmed' so the Booking shape holds.
 */
export const useBookingsForLink = (
  linkId: string | undefined,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  return useQuery({
    queryKey: ['bookings-for-link', linkId, rangeStart.toISOString(), rangeEnd.toISOString()],
    queryFn: async () => {
      if (!linkId) return [] as Booking[];
      // Cast through `any` — types.ts isn't regenerated for the
      // SEC-1 RPCs yet (same pattern as deliveryService).
      const { data, error } = await (supabase as any).rpc('public_booking_slots', {
        p_link_id: linkId,
        p_from: rangeStart.toISOString(),
        p_to: rangeEnd.toISOString(),
      });
      if (error) throw error;
      const rows = (data ?? []) as { id: string; start_at: string; end_at: string }[];
      return rows.map((r) => ({
        ...r,
        status: 'confirmed' as BookingStatus,
        booking_link_id: linkId,
      })) as Booking[];
    },
    enabled: !!linkId,
  });
};

/**
 * Create a booking from the public page.
 *
 * SEC-1 lockdown: the anon INSERT on bookings/inquiries and the org
 * timezone read are gone. public_create_booking() is now the single
 * door — it validates the link, guards double-booking server-side,
 * stamps both timezones, creates the booking + inquiry, links them,
 * and returns a one-time confirmation_token. The
 * send-booking-confirmation edge function REQUIRES that token, so an
 * anon can no longer fire arbitrary emails.
 *
 * Edge-function failures are non-fatal: the booking + inquiry are
 * already in the DB, so we log rather than tear down the success
 * state. They'll still see "you're booked"; the studio will see the
 * booking even if the email pipeline is briefly broken.
 */
export const useCreatePublicBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      bookingLink: BookingLink;
      startAt: Date;
      endAt: Date;
      clientName: string;
      clientEmail: string;
      clientPhone?: string;
      notes?: string;
    }) => {
      // Visitor's tz for stamping. `Intl` is available in every
      // modern browser; we fall back to UTC if it's somehow missing.
      const viewerTz =
        (typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat().resolvedOptions().timeZone) ||
        'UTC';

      // One RPC replaces the old org read + booking insert +
      // inquiry insert + link-back update. Cast through `any` —
      // types.ts isn't regenerated for the SEC-1 RPCs yet.
      const { data, error } = await (supabase as any).rpc('public_create_booking', {
        p_link_id: input.bookingLink.id,
        p_start: input.startAt.toISOString(),
        p_end: input.endAt.toISOString(),
        p_client_name: input.clientName,
        p_client_email: input.clientEmail,
        p_client_phone: input.clientPhone ?? null,
        p_notes: input.notes ?? null,
        p_viewer_timezone: viewerTz,
      });
      if (error) throw error;

      const result = data as {
        booking_id: string;
        inquiry_id: string;
        confirmation_token: string;
        start_at: string;
        end_at: string;
        studio_timezone: string | null;
      };

      // Fire the confirmation email + ICS attachment via the edge
      // function. We don't await — the visitor doesn't need to wait
      // on email send to see the success state — but we do log
      // failures so they show up in dev tools / Supabase function
      // logs for debugging. The confirmation_token authorizes
      // exactly one send (SEC-1).
      void supabase.functions
        .invoke('send-booking-confirmation', {
          body: {
            booking_id: result.booking_id,
            confirmation_token: result.confirmation_token,
          },
        })
        .then((res) => {
          if (res.error) {
            console.error(
              '[scheduler] send-booking-confirmation error:',
              res.error,
            );
          }
        })
        .catch((err) => {
          console.error('[scheduler] send-booking-confirmation threw:', err);
        });

      // Synthesize the Booking the page used to get back from the
      // insert — the RPC intentionally doesn't echo the full row.
      return {
        id: result.booking_id,
        org_id: input.bookingLink.org_id,
        booking_link_id: input.bookingLink.id,
        start_at: result.start_at,
        end_at: result.end_at,
        client_name: input.clientName,
        client_email: input.clientEmail,
        client_phone: input.clientPhone ?? null,
        notes: input.notes ?? null,
        status: 'confirmed',
        inquiry_id: result.inquiry_id,
        created_at: new Date().toISOString(),
      } as Booking;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings-for-link'] });
    },
  });
};

// ─── Slot computation ────────────────────────────────────────────

export interface AvailableSlot {
  start: Date;
  end: Date;
}

/**
 * Given availability rules + existing bookings for a link, generate
 * the open slots over the next `daysAhead` days. Slot start times
 * are computed as actual UTC instants — the studio's availability
 * rules are interpreted in the STUDIO's IANA timezone, then the
 * resulting instant gets rendered in the VIEWER's local timezone
 * by the UI's `toLocaleTimeString` calls. So a studio in Pacific
 * who says "Mon 9 AM" gets booked at 17:00 UTC, which the visitor
 * in Tokyo sees as 2 AM Tuesday — correct.
 *
 * Slot granularity = link.duration_minutes (no overlap support yet).
 * Buffers extend the "blocked" window around each existing booking
 * so a 60min booking with 15min buffer-after consumes 75min of
 * subsequent grid space.
 *
 * `studioTz` is required (default UTC) so the function never
 * silently falls back to the visitor's local clock — that's the
 * exact bug we're fixing.
 */
export function computeAvailableSlots({
  link,
  rules,
  existingBookings,
  fromDate,
  daysAhead = 7,
  studioTz = 'UTC',
}: {
  link: BookingLink;
  rules: AvailabilityRule[];
  existingBookings: Booking[];
  fromDate: Date;
  daysAhead?: number;
  /// IANA timezone of the studio. Availability rules are interpreted
  /// in this tz; resulting slot instants are absolute UTC.
  studioTz?: string;
}): AvailableSlot[] {
  const slots: AvailableSlot[] = [];

  // Today as a UTC date — we'll iterate calendar days but interpret
  // each "9 AM" wall-clock time inside studioTz, not the local
  // browser tz. So we pull year/month/day from the studio's
  // calendar perspective.
  const startStudio = getCalendarDateInTz(fromDate, studioTz);

  for (let dayOffset = 0; dayOffset < daysAhead; dayOffset++) {
    const day = addDaysToCalendarDate(startStudio, dayOffset);
    // day_of_week from the studio's own calendar (Sunday=0..Sat=6).
    const dow = dayOfWeekInTz(day, studioTz);
    const todayRules = rules.filter((r) => r.day_of_week === dow);
    if (todayRules.length === 0) continue;

    for (const rule of todayRules) {
      const [sh, sm] = rule.start_time.split(':').map((s) => parseInt(s, 10));
      const [eh, em] = rule.end_time.split(':').map((s) => parseInt(s, 10));
      // Convert wall-clock "sh:sm in studioTz on day" → UTC instant.
      let cursor = wallClockInTzToUtc(day.year, day.month, day.day, sh, sm, studioTz);
      const windowEnd = wallClockInTzToUtc(day.year, day.month, day.day, eh, em, studioTz);

      while (cursor.getTime() + link.duration_minutes * 60_000 <= windowEnd.getTime()) {
        const slotStart = new Date(cursor);
        const slotEnd = new Date(cursor.getTime() + link.duration_minutes * 60_000);

        // Filter out past slots
        const now = Date.now();
        if (slotEnd.getTime() <= now) {
          cursor = new Date(cursor.getTime() + link.duration_minutes * 60_000);
          continue;
        }

        // Skip if overlapping any existing booking (with buffers)
        const isBlocked = existingBookings.some((b) => {
          const bStart = new Date(b.start_at).getTime() - link.buffer_before_minutes * 60_000;
          const bEnd = new Date(b.end_at).getTime() + link.buffer_after_minutes * 60_000;
          return slotStart.getTime() < bEnd && slotEnd.getTime() > bStart;
        });

        if (!isBlocked) {
          slots.push({ start: slotStart, end: slotEnd });
        }

        cursor = new Date(cursor.getTime() + link.duration_minutes * 60_000);
      }
    }
  }

  return slots;
}

// ─── Timezone helpers ────────────────────────────────────────────
//
// We avoid pulling in date-fns-tz / luxon — the work we need
// (interpret a wall-clock time in a given IANA tz as a UTC instant,
// + reverse) is doable in 60 lines using Intl.DateTimeFormat. Keeps
// the bundle lean.

interface CalendarDate {
  year: number;
  month: number; // 1..12
  day: number;   // 1..31
}

/// What date is it RIGHT NOW in the given timezone? Returns y/m/d
/// integers (1-indexed month, like humans would write a date).
function getCalendarDateInTz(date: Date, tz: string): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parseInt(
    parts.find((p) => p.type === type)?.value ?? '0',
    10,
  );
  return { year: get('year'), month: get('month'), day: get('day') };
}

/// Add N calendar days to a y/m/d date. We delegate to JS Date for
/// the arithmetic (which knows about month lengths + leap years)
/// using UTC so we don't accidentally hit local-tz DST quirks.
function addDaysToCalendarDate(date: CalendarDate, days: number): CalendarDate {
  const utc = new Date(Date.UTC(date.year, date.month - 1, date.day));
  utc.setUTCDate(utc.getUTCDate() + days);
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

/// What day of week (0=Sun..6=Sat) is a calendar date in the given
/// timezone? We construct a UTC noon and read the weekday from Intl,
/// which is unambiguous regardless of DST shifts.
function dayOfWeekInTz(date: CalendarDate, tz: string): number {
  const utcNoon = new Date(Date.UTC(date.year, date.month - 1, date.day, 12, 0, 0));
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
  }).format(utcNoon);
  // Intl returns "Sun" / "Mon" / etc.
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
}

/// Convert a wall-clock time (year/month/day/hour/minute) in the
/// given IANA timezone to its UTC instant. Iterates twice to
/// converge across DST boundaries — first pass gets within an
/// hour, second nails the exact offset.
///
/// Why iterate: when DST shifts, the same wall-clock can map to
/// two UTC instants (fall-back) or zero (spring-forward). We
/// consistently pick the offset that the timezone considers "in
/// effect at the wall clock" — that's the standard scheduler
/// behavior most tools implement.
export function wallClockInTzToUtc(
  year: number,
  month: number, // 1..12
  day: number,   // 1..31
  hour: number,
  minute: number,
  tz: string,
): Date {
  // Start by treating the wall clock AS IF it were UTC. This is
  // the seed for our convergence loop.
  let candidate = new Date(Date.UTC(year, month - 1, day, hour, minute));

  for (let i = 0; i < 2; i++) {
    // Format the candidate UTC instant in the target tz. The
    // result tells us what wall clock the tz would display.
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(candidate);
    const get = (type: string) => parseInt(
      parts.find((p) => p.type === type)?.value ?? '0',
      10,
    );
    let tzHour = get('hour');
    if (tzHour === 24) tzHour = 0; // Intl quirk on some platforms

    const tzAsUtc = Date.UTC(
      get('year'), get('month') - 1, get('day'),
      tzHour, get('minute'),
    );
    const wantedAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    const drift = wantedAsUtc - tzAsUtc;
    if (drift === 0) break;
    candidate = new Date(candidate.getTime() + drift);
  }

  return candidate;
}

// ─── Helpers ─────────────────────────────────────────────────────

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  video: 'Video call',
  phone: 'Phone call',
  in_person: 'In person',
  custom: 'Custom',
};

/**
 * URL-safe slug generation from a name. Used as a starting point —
 * studio can override before saving.
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}
